/**
 * Sync guard - keeps a stale tab from overwriting newer changes made on another device.
 *
 * - Tags every update (PUT) request to the API with a per-tab client id, so the
 *   server can tell this tab's own rapid saves apart from other devices
 *   (see server/utils/revision.js). It is sent as a query parameter rather than
 *   a header, so it needs no CORS changes and older servers simply ignore it.
 * - When the server rejects an update because it was based on an outdated
 *   version (409 conflict), it asks the app to reload its data.
 *
 * Pages reload their data by listening with `useResync` (hooks/useRealtime.js).
 * The SocketProvider also requests a resync after reconnecting and when the
 * tab comes back after having been hidden for a while.
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5001";

export const RESYNC_EVENT = "flowmind:resync";

const createClientId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const CLIENT_ID = createClientId();

/**
 * Ask all mounted pages to reload their data from the server
 * @param {String} reason - "conflict", "reconnect" or "visible"
 * @param {String} [id] - For conflicts: ID of the document whose update was rejected
 */
export const requestResync = (reason, id = null) => {
  window.dispatchEvent(
    new CustomEvent(RESYNC_EVENT, { detail: { reason, id } }),
  );
};

const isApiRequest = (url) =>
  url.startsWith(API_URL) || url.startsWith("/api/");

export const installSyncGuard = () => {
  if (window.__flowmindSyncGuardInstalled) return;
  window.__flowmindSyncGuardInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || String(input);
    const method = (init.method || input?.method || "GET").toUpperCase();

    if (method !== "PUT" || !isApiRequest(url)) {
      return nativeFetch(input, init);
    }

    const taggedUrl = new URL(url, window.location.origin);
    taggedUrl.searchParams.set("clientId", CLIENT_ID);

    const response = await nativeFetch(
      input instanceof Request
        ? new Request(taggedUrl.toString(), input)
        : taggedUrl.toString(),
      init,
    );

    if (response.status === 409) {
      response
        .clone()
        .json()
        .then((body) => {
          if (body?.error === "conflict") {
            const id = new URL(url, window.location.origin).pathname
              .split("/")
              .pop();
            requestResync("conflict", id);
          }
        })
        .catch(() => {});
    }

    return response;
  };
};
